/**
 * "Add new version" from a document that is already in Franklin — the client
 * side of the `add_document_as_version` RPC (franklin-data-pipeline
 * 20260923104957).
 *
 * The client never writes `version_group` / `version` itself: nothing in the
 * database reacts to a lineage change, so the RPC owns the whole sequence
 * (locks, renumbering, re-electing both lineages' heads, the side tables).
 */
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { isMissingFunctionError } from "@/utils/postgrestErrors";
import { invalidateBuildingStorageQuota } from "@/domains/documents/services/buildingStorageQuota";
import { storageQuotaErrorMessage } from "@/domains/documents/services/buildingStorageQuota";
import {
  pinnedDocumentFromError,
  type PinnedDocument,
} from "@/domains/documents/services/pinnedDocuments";

export type AddAsVersionMode = "move" | "copy";

export interface AddAsVersionResult {
  action: "moved" | "copied";
  targetVersionGroup: string;
  documentIds: number[];
  firstVersion: number;
  lastVersion: number;
  headDocumentId: number;
  headVersion: number;
  renamed: Array<{ from: string; to: string }>;
  movedCount: number;
  deletedMovedCount: number;
  sourceVersionGroup: string | null;
  crossProject: boolean;
  anchorsRemoved: number;
  issueRootAction: "none" | "rekeyed" | "merged" | "detached";
}

/** Why a call failed, one per RPC error token plus the two client-side cases. */
export type AddAsVersionFailureReason =
  | "unavailable"
  | "forbiddenSource"
  | "forbiddenTarget"
  | "sameLineage"
  | "differentBuilding"
  | "sourceNotFound"
  | "targetNotFound"
  | "targetNotVersionable"
  | "sourceIsChild"
  | "containerCrossProject"
  | "storageQuota"
  | "pinned"
  | "unknown";

export type AddAsVersionOutcome =
  | { ok: true; result: AddAsVersionResult }
  | {
      ok: false;
      reason: AddAsVersionFailureReason;
      message: string;
      /** Set for `pinned`: the file that may not leave its folder. */
      pinned?: PinnedDocument;
    };

/** Narrowing helper: the app's tsconfig is not strict, so `!outcome.ok` does
 *  not discriminate the union on its own. */
export function isAddAsVersionFailure(
  outcome: AddAsVersionOutcome
): outcome is Extract<AddAsVersionOutcome, { ok: false }> {
  return outcome.ok === false;
}

/** The RPC raises these tokens as the exception message. */
const TOKEN_REASONS: Record<string, AddAsVersionFailureReason> = {
  forbidden_source: "forbiddenSource",
  forbidden_target: "forbiddenTarget",
  same_lineage: "sameLineage",
  different_building: "differentBuilding",
  source_not_found: "sourceNotFound",
  target_not_found: "targetNotFound",
  target_not_versionable: "targetNotVersionable",
  source_is_child: "sourceIsChild",
  container_cross_project: "containerCrossProject",
};

const ISSUE_ROOT_ACTIONS: ReadonlyArray<AddAsVersionResult["issueRootAction"]> = [
  "none",
  "rekeyed",
  "merged",
  "detached",
];

function isIssueRootAction(value: unknown): value is AddAsVersionResult["issueRootAction"] {
  return ISSUE_ROOT_ACTIONS.some((action) => action === value);
}

function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

// Once the RPC is known to be missing (app deployed ahead of the migration),
// stop offering the entry point for the rest of the session rather than
// letting every attempt fail the same way.
let unavailable = false;

/** False once this session has learned the database has no such RPC. */
export function isAddAsVersionAvailable(): boolean {
  return !unavailable;
}

/** Test seam: forget what this session learned. */
export function resetAddAsVersionAvailability(): void {
  unavailable = false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Validate and camel-case the jsonb the RPC returns. Null on a bad shape. */
export function parseAddAsVersionResult(data: unknown): AddAsVersionResult | null {
  if (!isRecord(data)) return null;
  const {
    action,
    target_version_group: targetVersionGroup,
    document_ids: documentIds,
    first_version: firstVersion,
    last_version: lastVersion,
    head_document_id: headDocumentId,
    head_version: headVersion,
    renamed,
    moved_count: movedCount,
    deleted_moved_count: deletedMovedCount,
    source_version_group: sourceVersionGroup,
    cross_project: crossProject,
    anchors_removed: anchorsRemoved,
    issue_root_action: issueRootAction,
  } = data;

  if (action !== "moved" && action !== "copied") return null;
  if (typeof targetVersionGroup !== "string") return null;
  if (!Array.isArray(documentIds) || !documentIds.every(isNumber)) return null;
  if (
    typeof firstVersion !== "number" ||
    typeof lastVersion !== "number" ||
    typeof headDocumentId !== "number" ||
    typeof headVersion !== "number"
  ) {
    return null;
  }
  if (!isIssueRootAction(issueRootAction)) return null;
  const renames = Array.isArray(renamed)
    ? renamed.filter(
        (r): r is { from: string; to: string } =>
          isRecord(r) && typeof r.from === "string" && typeof r.to === "string"
      )
    : [];

  return {
    action,
    targetVersionGroup,
    documentIds,
    firstVersion,
    lastVersion,
    headDocumentId,
    headVersion,
    renamed: renames,
    movedCount: typeof movedCount === "number" ? movedCount : 0,
    deletedMovedCount: typeof deletedMovedCount === "number" ? deletedMovedCount : 0,
    sourceVersionGroup: typeof sourceVersionGroup === "string" ? sourceVersionGroup : null,
    crossProject: crossProject === true,
    anchorsRemoved: typeof anchorsRemoved === "number" ? anchorsRemoved : 0,
    issueRootAction,
  };
}

/**
 * Make an existing document the next version of `targetDocumentId`'s lineage.
 *
 * `move` appends the source's whole lineage and relocates it; `copy` adds one
 * row from the chosen version. Never throws for an expected refusal — the
 * outcome names the reason so the caller can say it in the user's language.
 */
export async function addDocumentAsVersion(params: {
  targetDocumentId: number;
  sourceDocumentId: number;
  mode: AddAsVersionMode;
}): Promise<AddAsVersionOutcome> {
  const { data, error } = await supabase.rpc("add_document_as_version", {
    p_target_document_id: params.targetDocumentId,
    p_source_document_id: params.sourceDocumentId,
    p_mode: params.mode,
  });

  if (error) {
    const quotaMessage =
      storageQuotaErrorMessage(error.code) ?? storageQuotaErrorMessage(error.message);
    if (quotaMessage) return { ok: false, reason: "storageQuota", message: quotaMessage };
    // A move would take a pinned file out of its folder (guard_pinned_document).
    const pinned = pinnedDocumentFromError(error);
    if (pinned) return { ok: false, reason: "pinned", message: error.message, pinned };
    if (isMissingFunctionError(error)) {
      unavailable = true;
      return { ok: false, reason: "unavailable", message: error.message };
    }
    const reason = TOKEN_REASONS[error.message];
    if (reason) return { ok: false, reason, message: error.message };

    Sentry.captureException(new Error(`add_document_as_version failed: ${error.message}`), {
      tags: { feature: "add-new-version", stage: "add_document_as_version-rpc" },
      extra: { ...params, code: error.code },
    });
    return { ok: false, reason: "unknown", message: error.message };
  }

  const result = parseAddAsVersionResult(data);
  if (!result) {
    Sentry.captureMessage("add_document_as_version returned an unexpected shape", {
      level: "error",
      tags: { feature: "add-new-version", stage: "add_document_as_version-shape" },
      extra: { ...params },
    });
    return { ok: false, reason: "unknown", message: "Unexpected response from the server" };
  }
  invalidateBuildingStorageQuota();
  return { ok: true, result };
}

export interface LineageVersionStats {
  /** Highest version number in the lineage, trashed rows included — the RPCs
   *  number new versions from this. */
  maxVersion: number;
  /** Rows a move of this lineage appends (trashed ones too). */
  rowCount: number;
  /** Rows the user will see in the history afterwards. */
  liveCount: number;
}

/**
 * Version numbers are handed out from the lineage's MAX(version) including
 * trashed rows, so the head's own number undercounts whenever the newest
 * version is in the Trash. `documents_select` does not filter deleted rows,
 * so the caller can read the true figures.
 */
export async function fetchLineageVersionStats(versionGroup: string): Promise<LineageVersionStats> {
  const { data, error } = await supabase
    .from("documents")
    .select("version, is_deleted")
    .eq("version_group", versionGroup);
  if (error) throw new Error(error.message);

  const rows = data ?? [];
  return {
    maxVersion: rows.reduce((max, row) => Math.max(max, row.version ?? 0), 0),
    rowCount: rows.length,
    liveCount: rows.filter((row) => row.is_deleted !== true).length,
  };
}
